import type {
  CalendarEventDto,
  CreateCalendarEventBody,
  CreateDayOffBody,
  ListCalendarEventsQuery,
  ListDayOffsQuery,
  ListLinkedItemsQuery,
  ListLinkedItemsResult,
  UpdateCalendarEventBody,
  WorkCalendarDayOffDto,
} from "@/modules/management/calendar/domain/types";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

const base = `${config.apiUrl}/management/calendar`;

/** ໃຫ້ API ໃຊ້ end-of-day ເພື່ອໃຫ້ string compare ກັບ startAt/endAt (ISO) ຖືກຕ້ອງ — ວັນດຽວ/ສັບປະດານຈຶ່ງຂຶ້ນ event */
function buildEventsQuery(q: ListCalendarEventsQuery): string {
  const p = new URLSearchParams();
  const start =
    q.start.includes("T") ? q.start : `${q.start}T00:00:00.000`;
  const end = q.end.includes("T") ? q.end : `${q.end}T23:59:59.999`;
  p.set("start", start);
  p.set("end", end);
  if (q.userId) p.set("userId", q.userId);
  return p.toString();
}

function buildDayOffsQuery(q: ListDayOffsQuery): string {
  const p = new URLSearchParams();
  if (q.year != null) p.set("year", String(q.year));
  if (q.start) p.set("start", q.start);
  if (q.end) p.set("end", q.end);
  return p.toString();
}

export const managementCalendarApi = {
  listEvents(query: ListCalendarEventsQuery): Promise<CalendarEventDto[]> {
    return fetcher.get<CalendarEventDto[]>(
      `${base}/events?${buildEventsQuery(query)}`,
    );
  },

  getEventById(id: string): Promise<CalendarEventDto> {
    return fetcher.get<CalendarEventDto>(`${base}/events/${encodeURIComponent(id)}`);
  },

  createEvent(body: CreateCalendarEventBody): Promise<{ id: string }> {
    return fetcher.post<{ id: string }>(`${base}/events`, body);
  },

  updateEvent(
    id: string,
    body: UpdateCalendarEventBody,
  ): Promise<{ ok: boolean }> {
    return fetcher.put<{ ok: boolean }>(
      `${base}/events/${encodeURIComponent(id)}`,
      body,
    );
  },

  deleteEvent(id: string): Promise<{ ok: boolean }> {
    return fetcher.delete<{ ok: boolean }>(
      `${base}/events/${encodeURIComponent(id)}`,
    );
  },

  listLinkedItems(query: ListLinkedItemsQuery): Promise<ListLinkedItemsResult> {
    const p = new URLSearchParams();
    const start = query.start.includes("T") ? query.start.slice(0, 10) : query.start.slice(0, 10);
    const end = query.end.includes("T") ? query.end.slice(0, 10) : query.end.slice(0, 10);
    p.set("start", start);
    p.set("end", end);
    p.set("userId", query.userId);
    return fetcher.get<ListLinkedItemsResult>(
      `${base}/linked-items?${p.toString()}`,
    );
  },

  listDayOffs(query: ListDayOffsQuery): Promise<WorkCalendarDayOffDto[]> {
    const q = buildDayOffsQuery(query);
    return fetcher.get<WorkCalendarDayOffDto[]>(
      q ? `${base}/work-calendar?${q}` : `${base}/work-calendar`,
    );
  },

  createDayOff(body: CreateDayOffBody): Promise<{ id: string }> {
    return fetcher.post<{ id: string }>(`${base}/work-calendar`, body);
  },

  deleteDayOff(id: string): Promise<{ ok: boolean }> {
    return fetcher.delete<{ ok: boolean }>(
      `${base}/work-calendar/${encodeURIComponent(id)}`,
    );
  },
};
